import React, { FC, useEffect, useRef } from 'react'
import { useDeletePatientMutation } from '../../../../../core/api/patientApi'
import { ModalConfirmAction } from '../../../../common/ModalConfirmAction'
import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { message, notification } from 'antd'
import { useTranslation } from 'react-i18next'

interface ModalDeletePatinetProps {
  patient: DecryptedPatient
  isVisible: boolean
  onClose: () => void
}

export const ModalDeletePatient: FC<ModalDeletePatinetProps> = ({ patient, isVisible, onClose }) => {
  const { t } = useTranslation()
  const [deletePatient, { isError: isDeletePatientError, error, isSuccess: isPatientDeletedSuccessfully, isLoading: isPatientDeletingLoading }] = useDeletePatientMutation()

  // ✅ use hook APIs + render their context holders
  const [messageApi, messageCtx] = message.useMessage()
  const [notificationApi, notificationCtx] = notification.useNotification()

  const closeTmr = useRef<ReturnType<typeof setTimeout> | null>(null)
  const msgKey = 'delete-patient'

  const openNotification = (type: 'error', title: string, description: string) => {
    notificationApi.open({
      type,
      message: title,
      description,
      duration: 0,
      key: 'delete-patient-notif',
    })
    setTimeout(() => notificationApi.destroy('delete-patient-notif'), 2500)
  }

  useEffect(() => {
    if (isPatientDeletingLoading) {
      messageApi.open({ key: msgKey, type: 'loading', content: t('modalDeletePatient.deleting'), duration: 0 })
    }

    if (isPatientDeletedSuccessfully) {
      messageApi.open({ key: msgKey, type: 'success', content: t('modalDeletePatient.deleted') })
      closeTmr.current = setTimeout(onClose, 1500)
    }

    if (isDeletePatientError) {
      messageApi.destroy(msgKey)
      console.log({ error })
      openNotification('error', t('modalDeletePatient.cannotDeleteTitle'), t('modalDeletePatient.cannotDeleteDesc'))
      closeTmr.current = setTimeout(onClose, 1500)
    }

    return () => {
      if (closeTmr.current) {
        clearTimeout(closeTmr.current)
        closeTmr.current = null
      }
    }
    // include onClose to avoid stale closure
  }, [isPatientDeletingLoading, isPatientDeletedSuccessfully, isDeletePatientError, messageApi, notificationApi, onClose, t, error])

  return (
    <>
      {messageCtx}
      {notificationCtx}

      <ModalConfirmAction
        title={t('modalDeletePatient.modalTitle')}
        description={t('modalDeletePatient.modalDescription')}
        yesBtnTitle={t('modalDeletePatient.yesBtn')}
        noBtnTitle={t('modalDeletePatient.noBtn')}
        onYesClick={() => deletePatient(patient)}
        onNoClick={onClose}
        isVisible={isVisible}
        mode="danger"
      />
    </>
  )
}
